import { createSlice, nanoid } from "@reduxjs/toolkit";

const createProcedure = (title = "") => ({
  id: nanoid(),
  title,
  notes: "",
  revision: "",
  status: "DRAFT",
  description: "",
  create_date: new Date().toJSON(),
  update_date: new Date().toJSON(),
  items: [],
});

export const GROUP = ["Pass/Fail", "Yes/No", "Task Item", "Custom"];
export const INSTRUCTION = ["Instruction"];
// export const CUSTOM = ["Custom"];
export const SUB_SECTION = ["Sub-Section"];
export const ITEM_OPTIONS = [
  ...INSTRUCTION,
  ...GROUP,
  // ...CUSTOM,
  ...SUB_SECTION,
];

const createSection = () => ({
  id: nanoid(),
  title: "",
  type: "SECTION",
  steps: [],
  items: [],
});

const createGroup = (type, sectionId) => {
  return {
    id: nanoid(),
    section_id: sectionId,
    type,
    items: [],
  };
};
const createGroupItem = (type, sectionId) => {
  if (type === "TASK ITEM") {
    return {
      id: nanoid(),
      section_id: sectionId,
      type,
      label: "",
      description: "",
      na: false,
      warn: false,
      skip: false,
      repeat: 1,
      checkbox_label: "Completed",
    };
  }
  if (type === "YES/NO") {
    return {
      id: nanoid(),
      section_id: sectionId,
      type,
      label: "",
      description: "",
      test_types: "AFFIRMATIVE_PASS",
      na: false,
      warn: false,
      skip: false,
    };
  }
  if (type === "CUSTOM") {
    return {
      id: nanoid(),
      section_id: sectionId,
      type,
      label: "",
      description: "",
      url: "",
      configuration: "",
      na: false,
      warn: false,
      skip: false,
    };
  }
  return {
    id: nanoid(),
    section_id: sectionId,
    type,
    label: "",
    description: "",
    na: false,
    warn: false,
    skip: false,
  };
};

const createInstruction = (type, sectionId) => {
  const id = nanoid();
  return {
    id,
    section_id: sectionId,
    type,
    content: "",
    title: "",
    infoPanel: "none",
  };
};

// const createCustom = (type, sectionId) => {
//   const id = nanoid();
//   return {
//     id,
//     section_id: sectionId,
//     type,
//     label: "",
//     description: "",
//     url: "",
//     configuration: "",
//   };
// };

const createSubSection = (type, sectionId) => {
  return {
    id: nanoid(),
    section_id: sectionId,
    title: "",
    type,
    steps: [],
    items: [],
  };
};

const createItem = (sectionId, name = "") => {
  const type = name.toUpperCase();
  switch (type) {
    case "INSTRUCTION":
      return createInstruction(type, sectionId);
    case "SUB-SECTION":
      return createSubSection(type, sectionId);
    // case "CUSTOM":
    //   return createCustom(type, sectionId);
    default:
      return createGroupItem(type, sectionId);
  }
};

const initialState = createProcedure();

export const procedureSlice = createSlice({
  name: "procedure",
  initialState,
  reducers: {
    setProcedure: (state, action) => {
      state["id"] = action.payload.data.id;
      state["title"] = action.payload.data.title;
      state["notes"] = action.payload.data.notes;
      state["revision"] = action.payload.data.revision;
      state["status"] = action.payload.data.status;
      state["description"] = action.payload.data.description;
      state["create_date"] = action.payload.data.create_date;
      state["update_date"] = action.payload.data.update_date;
      state["items"] = action.payload.data.items;
    },
    clearProcedure: (state, action) => {
      state["id"] = nanoid();
      state["title"] = "";
      state["notes"] = "";
      state["revision"] = "";
      state["status"] = "DRAFT";
      state["description"] = "";
      state["create_date"] = new Date().toJSON();
      state["update_date"] = new Date().toJSON();
      state["items"] = [];
    },
    handleProcedure: (state, action) => {
      state[action.payload.name] = action.payload.value;
    },
    // Section
    addSection: (state, action) => {
      const section = createSection();
      state.items.push(section);
    },
    removeSection: (state, action) => {
      state.items = state.items.filter(
        (section) => section.id !== action.payload.sectionId
      );
    },
    setSectionTitle: (state, action) => {
      let section = state.items.find(
        (section) => section.id === action.payload.sectionId
      );
      if (action.payload.subSectionId) {
        let subSection = section.items.find(
          (section) => section.id === action.payload.subSectionId
        );
        subSection.title = action.payload.title;
        return;
      }
      section.title = action.payload.title;
    },
    // Sub Section
    // addSubSection: (state, action) => {
    //   const sections = state.items;
    //   const sectionId = action.payload.sectionId;
    //   const newSubSection = createSubSection(sectionId);
    //   const section = Array.from(sections).find(
    //     (section) => section.id === sectionId
    //   );
    //   section.items = [...section.items, newSubSection];
    // },
    addSectionItem: (state, action) => {
      const section = state.items.find(
        (section) => section.id === action.payload.sectionId
      );
      if (action.payload.subSectionId) {
        const subSection = section.items.find(
          (section) => section.id === action.payload.subSectionId
        );
        if (!isGroup(action.payload.name)) {
          const item = createItem(
            action.payload.sectionId,
            action.payload.name
          );
          subSection.items.push(item);
          subSection.steps.push(item.id);
          return;
        }

        if (!grouped(subSection.items, action.payload.name)) {
          const group = createGroup("GROUP", action.payload.sectionId);
          const item = createItem(
            action.payload.sectionId,
            action.payload.name
          );
          group.items.push({ ...item, _group: group.id });
          subSection.items.push(group);
          subSection.steps.push(item.id);
          return;
        } else {
          // Group
          const group = subSection.items[subSection.items.length - 1];
          const item = createItem(
            action.payload.sectionId,
            action.payload.name
          );
          group.items.push({ ...item, _group: group.id });
          subSection.steps.push(item.id);
          return;
        }
      }
      if (!isGroup(action.payload.name)) {
        const item = createItem(action.payload.sectionId, action.payload.name);
        section.items.push(item);
        section.steps.push(item.id);
        return;
      }

      if (!grouped(section.items, action.payload.name)) {
        const group = createGroup("GROUP", action.payload.sectionId);
        const item = createItem(action.payload.sectionId, action.payload.name);
        group.items.push({ ...item, _group: group.id });
        section.items.push(group);
        section.steps.push(item.id);
        return;
      } else {
        // Group
        const group = section.items[section.items.length - 1];
        const item = createItem(action.payload.sectionId, action.payload.name);
        group.items.push({ ...item, _group: group.id });
        section.steps.push(item.id);
        return;
      }
    },
    updateSectionItems: (state, action) => {
      // console.log(action)
      const section = state.items.find(
        (section) => section.id === action.payload.sectionId
      );

      if (action.payload.subSectionId) {
        const subSection = section.items.find(
          (section) => section.id === action.payload.subSectionId
        );
        let item = subSection["items"].find(
          (sectionItem) => sectionItem.id === action.payload.sectionItemId
        );
        if (action.payload.type === "GROUP") {
          item = item["items"].find(
            (groupItem) => groupItem.id === action.payload.id
          );
        }
        item[action.payload.name] = action.payload.value;
        return;
      }

      let item = section["items"].find(
        (sectionItem) => sectionItem.id === action.payload.sectionItemId
      );
      if (action.payload.type === "GROUP") {
        item = item["items"].find(
          (groupItem) => groupItem.id === action.payload.id
        );
      }
      item[action.payload.name] = action.payload.value;
    },
    deleteItem: (state, action) => {
      // console.log(action)
      let section = state.items.find(
        (section) => section.id === action.payload.sectionId
      );
      if (action.payload.subSectionId) {
        const subSection = section.items.find(
          (section) => section.id === action.payload.subSectionId
        );
        if (!action.payload.id) {
          subSection.steps = subSection.steps.filter(
            (v) => v !== action.payload.sectionItemId
          );
          section["items"] = subSection.items.filter(
            (section) => section.id !== action.payload.sectionItemId
          );
        }
        if (action.payload.type === "GROUP") {
          let group = subSection.items.find(
            (section) => section.id === action.payload.sectionItemId
          );
          subSection.steps = subSection.steps.filter(
            (v) => v !== action.payload.id
          );
          if (Array.from(group["items"]).length <= 1) {
            subSection["items"] = subSection.items.filter(
              (section) => section.id !== action.payload.sectionItemId
            );
          } else {
            group["items"] = group.items.filter(
              (section) => section.id !== action.payload.id
            );
          }
        }
        return;
      }
      if (!action.payload.id) {
        section.steps = section.steps.filter(
          (v) => v !== action.payload.sectionItemId
        );
        section["items"] = section.items.filter(
          (section) => section.id !== action.payload.sectionItemId
        );
      }
      if (action.payload.type === "GROUP") {
        let group = section.items.find(
          (section) => section.id === action.payload.sectionItemId
        );
        section.steps = section.steps.filter((v) => v !== action.payload.id);
        if (Array.from(group["items"]).length <= 1) {
          section["items"] = section.items.filter(
            (section) => section.id !== action.payload.sectionItemId
          );
        } else {
          group["items"] = group.items.filter(
            (section) => section.id !== action.payload.id
          );
        }
      }
    },
  },
});
const isGroup = (type) => {
  return GROUP.indexOf(type) > -1;
};
const grouped = (items, type) => {
  if (!items.length) {
    return false;
  }
  if (items[items.length - 1]["type"] === "GROUP") {
    return true;
  } else {
    return false;
  }

  //   if (GROUP.map((v) => v.toUpperCase()).indexOf(items[items.length - 1]) > -1) {
  //     return false;
  //   } else {
  //     return true;
  //   }
};
